import React, { useState, useEffect } from "react";
import {
  C, ROLE_NAMES, ROLE_PERMISSIONS, ROLE_DESCRIPTIONS, MODULE_LABELS, SCOPE_LABELS,
  getPerms, mergePermissions, getUserRoles, getPrimaryRole, hasRole
} from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, useToast, exportToCSV
} from "./erp-shared";
import { getAllUsers, toggleUserStatus, addAuditLog, createNotification } from "./supabaseClient";
import { adminCreateUser, authResetPassword, updateUserRoles } from "./supabaseAuth";
import {
  UserCog, Users, UserPlus, Plus, Search, CheckCircle,
  AlertCircle, Shield, Lock, Edit,
  Key, Activity, LogIn, Download,
  ShieldCheck, ShieldOff, XCircle
} from "lucide-react";

export default function UsersPage({ user }) {
  const [tab, setTab] = useState("users");
  const perms = getPerms(user);

  const tabs = [
    { key: "users", label: "المستخدمون", icon: Users },
    { key: "roles", label: "الأدوار والصلاحيات", icon: Shield },
    { key: "audit", label: "سجل النظام", icon: Activity },
    { key: "sessions", label: "الجلسات النشطة", icon: LogIn },
  ];

  return (
    <div>
      <ModuleHeader icon={UserCog} title="المستخدمون" subtitle="إدارة حسابات المستخدمين والأدوار والصلاحيات وسجل النظام" />
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "users" && <UsersTab canEdit={perms.canEdit || perms.adminOnly} currentUser={user} />}
      {tab === "roles" && <RolesTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "sessions" && <SessionsTab />}
    </div>
  );
}

/* ─── Users Tab ─── */
const INITIAL_USER = { name: "", email: "", phone: "", roles: [], password: "" };

function UsersTab({ canEdit, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [createdUserInfo, setCreatedUserInfo] = useState(null);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_USER);
  const [rolesTarget, setRolesTarget] = useState(null); // المستخدم الجاري تعديل أدواره
  const [showToast, ToastView] = useToast();

  const roles = Object.keys(ROLE_NAMES);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    setPageError(null);
    try {
      const data = await getAllUsers();
      setUsers(data || []);
    } catch (err) {
      setPageError("تعذّر تحميل قائمة المستخدمين");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }

  function closeAddModal() {
    setShowAdd(false);
    setForm(INITIAL_USER);
    setCreatedUserInfo(null);
  }

  async function handleSave() {
    if (!form.name || !form.email) {
      showToast("الاسم والبريد حقول مطلوبة", "error");
      return;
    }
    // دور واحد على الأقل — النظام لا يقبل مستخدمًا بلا صلاحيات
    if (!form.roles.length) {
      showToast("اختر دورًا واحدًا على الأقل", "error");
      return;
    }
    setLoading(true);
    const result = await adminCreateUser({
      email: form.email,
      name: form.name,
      roles: form.roles,
      role: form.roles[0],
      phone: form.phone || undefined,
      temporaryPassword: form.password || undefined,
    });
    setLoading(false);

    if (!result.success) {
      showToast(result.error, "error");
      return;
    }

    setCreatedUserInfo({
      email: form.email,
      password: result.temporaryPassword,
      usedDefault: result.usedDefaultPassword,
    });

    await loadUsers();
    if (currentUser?.id) {
      await addAuditLog(
        currentUser.id,
        currentUser.name,
        "create_user",
        `أُنشئ موظف جديد: ${form.name} (${form.email}) — الأدوار: ${form.roles.map(r => ROLE_NAMES[r]).join("، ")}`
      );
    }
    showToast("تم إنشاء الموظف بنجاح", "success");
  }

  async function handleToggle(row) {
    const newStatus = !row.is_active;
    try {
      await toggleUserStatus(row.id, newStatus);
      showToast(newStatus ? "تم التفعيل" : "تم التعطيل", "success");
      await loadUsers();
      if (currentUser?.id) {
        await addAuditLog(
          currentUser.id,
          currentUser.name,
          "update_user",
          `${newStatus ? "فعّل" : "عطّل"} حساب ${row.email}`
        );
      }
    } catch (e) {
      showToast("تعذّر التحديث", "error");
    }
  }

  async function handleResetPassword(row) {
    if (!window.confirm(`إرسال رابط إعادة تعيين كلمة المرور إلى ${row.email}؟`)) {
      return;
    }
    try {
      const result = await authResetPassword(row.email);
      if (result.error) {
        showToast(`تعذّر الإرسال: ${result.error.message || result.error}`, "error");
      } else {
        showToast("تم إرسال رابط الإعادة على البريد", "success");
        if (currentUser?.id) {
          await addAuditLog(
            currentUser.id,
            currentUser.name,
            "reset_password",
            `طُلبت إعادة تعيين كلمة المرور لـ ${row.email}`
          );
        }
      }
    } catch (e) {
      showToast("تعذّر الإرسال", "error");
    }
  }
  function handleExport() {
    exportToCSV(filtered, [
      { key: "name",    header: "الاسم" },
      { key: "email",   header: "البريد الإلكتروني" },
      { key: "phone",   header: "الهاتف" },
      { key: "roles",   header: "الأدوار", value: u => getUserRoles(u).map(r => ROLE_NAMES[r] || r).join(" | ") },
      { key: "department", header: "القسم" },
      { key: "is_active",  header: "الحالة", value: u => (u.is_active ? "فعّال" : "معطّل") },
      { key: "last_login", header: "آخر دخول" },
      { key: "created_at", header: "تاريخ الإنشاء" },
    ], "المستخدمون");
    showToast(`تم تصدير ${filtered.length} مستخدم`, "success");
  }

  const filtered = users.filter(u => {
    const ms = u.name.includes(search) || u.email.includes(search);
    const mr = filterRole === "all" || getUserRoles(u).includes(filterRole);
    return ms && mr;
  });

  const USER_STATUSES = [
    { key: "active", label: "فعّال", color: C.success },
    { key: "inactive", label: "معطّل", color: C.danger },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="إجمالي المستخدمين" value={users.length} icon={Users} color="#475569" />
        <KPICard title="فعّالين" value={users.filter(u => u.is_active).length} icon={CheckCircle} color={C.success} />
        <KPICard title="معطّلين" value={users.filter(u => !u.is_active).length} icon={XCircle} color={C.danger} />
        <KPICard title="الأدوار المستخدمة" value={[...new Set(users.flatMap(u => getUserRoles(u)))].length} icon={Shield} color="#7c3aed" />
      </div>

      {pageError && (
        <Card style={{ padding: 12, marginBottom: 16, background: `${C.danger}10`, border: `1px solid ${C.danger}40`, color: C.danger, fontSize: 13 }}>
          {pageError}
        </Card>
      )}

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={S.toolbar}>
          <div style={S.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو البريد..." value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={S.filterSelect}>
              <option value="all">جميع الأدوار</option>
              {roles.map(r => <option key={r} value={r}>{ROLE_NAMES[r]}</option>)}
            </select>
            <Btn icon={Download} variant="ghost" size="sm" onClick={handleExport}>تصدير</Btn>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة مستخدم</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "المستخدم", render: (v, row) => (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  ...S.tableAvatar,
                  background: row.is_active ? `linear-gradient(135deg, ${C.primary}, ${C.teal})` : C.textLight,
                }}>{v.charAt(0)}</div>
                <div>
                  <span style={{ fontWeight: 600, display: "block" }}>{v}</span>
                  <span style={{ fontSize: 11, color: C.textLight }} dir="ltr">{row.email}</span>
                </div>
              </div>
            )},
            { key: "role", label: "الأدوار", width: "220px", render: (v, row) => {
              const rs = getUserRoles(row);
              const primary = getPrimaryRole(row);
              const extra = rs.filter(r => r !== primary);
              return (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={S.rolePrimary}>{ROLE_NAMES[primary] || primary || "—"}</span>
                  {extra.map(r => (
                    <span key={r} style={S.roleExtra}>+{ROLE_NAMES[r] || r}</span>
                  ))}
                </div>
              );
            }},
            { key: "is_active", label: "الحالة", width: "90px", render: v => (
              <StatusBadge status={v ? "active" : "inactive"} statuses={USER_STATUSES} />
            )},
            { key: "last_login", label: "آخر دخول", width: "120px", render: v => v || "لم يسجّل دخول" },
            { key: "created_at", label: "تاريخ الإنشاء", width: "110px" },
            { key: "actions", label: "إجراءات", width: "130px", render: (v, row) => canEdit ? (
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={e => { e.stopPropagation(); setRolesTarget(row); }} style={S.actionBtn} title="تعديل الصلاحيات">
                  <Shield size={14} color="#7c3aed" />
                </button>
                <button onClick={e => { e.stopPropagation(); handleResetPassword(row); }} style={S.actionBtn} title="إعادة تعيين كلمة المرور">
                  <Key size={14} color={C.warning} />
                </button>
                <button onClick={e => { e.stopPropagation(); handleToggle(row); }} style={S.actionBtn} title={row.is_active ? "تعطيل" : "تفعيل"}>
                  {row.is_active ? <ShieldOff size={14} color={C.danger} /> : <ShieldCheck size={14} color={C.success} />}
                </button>
              </div>
            ) : null },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {/* Detail */}
      {selected && (
        <Modal title={`مستخدم — ${selected.name}`} onClose={() => setSelected(null)} width={550}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={S.userHeader}>
              <div style={S.userAvatarLg}>{selected.name.charAt(0)}</div>
              <div>
                <h3 style={{ margin: 0, color: C.primary }}>{selected.name}</h3>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", margin: "4px 0" }}>
                  {getUserRoles(selected).map(r => (
                    <span key={r} style={S.rolePrimary}>{ROLE_NAMES[r] || r}</span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={S.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.email}</span></div>
              <div style={S.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone || "—"}</span></div>
              <div style={S.detailRow}><strong>الحالة:</strong> {selected.is_active ? "فعّال" : "معطّل"}</div>
              <div style={S.detailRow}><strong>آخر دخول:</strong> {selected.last_login || "لم يسجّل"}</div>
              <div style={S.detailRow}><strong>تاريخ الإنشاء:</strong> {selected.created_at}</div>
              <div style={S.detailRow}><strong>تغيير كلمة المرور:</strong> {selected.must_change_password ? "مطلوب" : "لا"}</div>
            </div>
            <div style={S.detailRow}>
              <strong>الصلاحيات:</strong>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                {(mergePermissions(getUserRoles(selected)).modules || []).map((m, i) => (
                  <span key={i} style={S.permTag}>{MODULE_LABELS[m] || m}</span>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              {mergePermissions(getUserRoles(selected)).canEdit && <span style={{ ...S.permBadge, background: `${C.teal}15`, color: C.teal }}>تعديل</span>}
              {mergePermissions(getUserRoles(selected)).canSign && <span style={{ ...S.permBadge, background: `${C.accent}15`, color: "#8b6914" }}>توقيع</span>}
              {mergePermissions(getUserRoles(selected)).adminOnly && <span style={{ ...S.permBadge, background: `${C.danger}15`, color: C.danger }}>مشرف</span>}
            </div>
          </div>
        </Modal>
      )}

      {/* تعديل الصلاحيات */}
      {rolesTarget && (
        <RolesModal
          target={rolesTarget}
          currentUser={currentUser}
          onClose={() => setRolesTarget(null)}
          onSaved={async (msg) => {
            setRolesTarget(null);
            showToast(msg, "success");
            await loadUsers();
          }}
          onError={msg => showToast(msg, "error")}
        />
      )}

      {/* Add User */}
      {showAdd && (
        <Modal title="إضافة مستخدم جديد" onClose={closeAddModal} width={600}>
          {createdUserInfo ? (
            <Card style={{ background: "#FFF8E1", border: "2px solid #DCC160", padding: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>تم إنشاء الموظف بنجاح</div>
              <div>البريد: <strong>{createdUserInfo.email}</strong></div>
              <div>كلمة المرور المؤقتة: <strong style={{ fontFamily: "monospace", fontSize: 16 }}>{createdUserInfo.password}</strong></div>
              <div style={{ fontSize: 13, color: "#5a7b8f", marginTop: 8 }}>
                {createdUserInfo.usedDefault
                  ? "تم استخدام كلمة المرور الافتراضية. الموظف مطالب بتغييرها أول دخول."
                  : "تم استخدام كلمة المرور المخصصة التي أدخلتها."}
              </div>
              <Btn onClick={closeAddModal} style={{ marginTop: 12 }}>إغلاق</Btn>
            </Card>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="الاسم الكامل" required placeholder="اسم المستخدم" value={form.name} onChange={v => setForm({ ...form, name: v })} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="البريد الإلكتروني" required placeholder="user@bnr.org.sa" value={form.email} onChange={v => setForm({ ...form, email: v })} />
                <Field label="الهاتف" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              </div>
              <Field label="كلمة المرور المؤقتة (اختياري — افتراضي Bnr@2026)" placeholder="6 أحرف على الأقل" value={form.password} onChange={v => setForm({ ...form, password: v })} />

              {/* الأدوار والصلاحيات — يُقبل أكثر من دور */}
              <div>
                <div style={S.sectionLabel}>
                  <Shield size={15} color="#7c3aed" />
                  <span>الأدوار والصلاحيات</span>
                  <span style={{ color: C.danger }}>*</span>
                </div>
                <div style={S.rolesCheckGrid}>
                  {roles.map(r => {
                    const on = form.roles.includes(r);
                    return (
                      <label key={r} style={{ ...S.roleCheckItem, ...(on ? S.roleCheckItemOn : null) }}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setForm(f => ({
                            ...f,
                            roles: on ? f.roles.filter(x => x !== r) : [...f.roles, r],
                          }))}
                          style={{ accentColor: "#7c3aed", width: 15, height: 15, flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 12.5, fontWeight: on ? 600 : 500 }}>{ROLE_NAMES[r]}</span>
                      </label>
                    );
                  })}
                </div>
                {form.roles.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <PermissionsPreview roles={form.roles} />
                  </div>
                )}
              </div>
              <div style={{ padding: "10px 14px", background: `${C.warning}10`, borderRadius: 8, fontSize: 13, color: C.warning, display: "flex", alignItems: "center", gap: 8 }}>
                <AlertCircle size={16} />
                سيُطلب من المستخدم تغيير كلمة المرور عند أول دخول
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <Btn icon={UserPlus} onClick={handleSave} disabled={loading}>إنشاء الحساب</Btn>
                <Btn variant="ghost" onClick={closeAddModal}>إلغاء</Btn>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

/* ─── معاينة الصلاحيات الفعلية بعد الدمج ─── */
function PermissionsPreview({ roles }) {
  const p = mergePermissions(roles);
  const mods = p.modules || [];
  return (
    <div style={S.previewBox}>
      <div style={S.previewTitle}>الصلاحيات الفعلية بعد الدمج</div>
      <div style={S.previewRow}>
        <span style={S.previewKey}>التبويبات</span>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {mods.length === 0
            ? <span style={{ fontSize: 12, color: C.textLight }}>لا شيء</span>
            : mods.map(m => <span key={m} style={S.permTag}>{MODULE_LABELS[m] || m}</span>)}
        </div>
      </div>
      <div style={S.previewRow}>
        <span style={S.previewKey}>النطاق</span>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{SCOPE_LABELS[p.scope] || p.scope}</span>
      </div>
      <div style={S.previewRow}>
        <span style={S.previewKey}>المالية</span>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{p.finance === false ? "بدون وصول" : (SCOPE_LABELS[p.finance] || p.finance)}</span>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
        {p.canEdit && <span style={{ ...S.permBadge, background: `${C.teal}15`, color: C.teal }}>تعديل</span>}
        {p.canSign && <span style={{ ...S.permBadge, background: `${C.accent}15`, color: "#8b6914" }}>توقيع</span>}
        {p.adminOnly && <span style={{ ...S.permBadge, background: `${C.danger}15`, color: C.danger }}>مشرف</span>}
        {p.tabs === "*" && <span style={{ ...S.permBadge, background: "#7c3aed15", color: "#7c3aed" }}>كل التبويبات</span>}
      </div>
    </div>
  );
}

/* ─── نافذة تعديل الأدوار — التبديل الحيّ يحدّث المعاينة فوراً ─── */
function RolesModal({ target, currentUser, onClose, onSaved, onError }) {
  const allRoles = Object.keys(ROLE_NAMES);
  const [sel, setSel] = useState(() => getUserRoles(target));
  const [saving, setSaving] = useState(false);

  // مدير النظام لا يستطيع نزع دور system من نفسه (قفل الباب على نفسه)
  const isSelf = String(target.id) === String(currentUser?.id);
  const lockSystem = isSelf && hasRole(target, "system");

  function toggle(r) {
    if (lockSystem && r === "system") return;
    setSel(cur => cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r]);
  }

  async function handleSave() {
    if (sel.length === 0) {
      onError("لا يمكن ترك المستخدم بدون أي دور");
      return;
    }
    setSaving(true);
    const res = await updateUserRoles(target.id, sel);
    if (!res.success) {
      setSaving(false);
      onError(res.error || "تعذّر حفظ الأدوار");
      return;
    }

    const names = sel.map(r => ROLE_NAMES[r] || r).join("، ");

    // إشعار الموظف — user_id لازم يكون auth.users.id (سياسة RLS تقارنه بـ auth.uid())
    const recipient = target.auth_user_id || target.id;
    try {
      await createNotification({
        user_id: recipient,
        title: "تم تحديث صلاحياتك",
        message: `حدّث ${currentUser?.name || "مدير النظام"} صلاحياتك. أدوارك الحالية: ${names}. تُطبَّق بالكامل بعد تسجيل الخروج والدخول مجدداً.`,
        type: "info",
        related_id: null,
        created_by: currentUser?.auth_user_id || currentUser?.authId || currentUser?.id || null,
      });
    } catch (_e) { /* الإشعار ثانوي — لا يُفشل الحفظ */ }

    try {
      if (currentUser?.id) {
        await addAuditLog(
          currentUser.id,
          currentUser.name,
          "update_roles",
          `عدّل أدوار ${target.name} (${target.email}) إلى: ${names}`
        );
      }
    } catch (_e) { /* السجل ثانوي */ }

    setSaving(false);
    onSaved(res.warning ? `تم حفظ الأدوار. ${res.warning}` : "تم حفظ الأدوار");
  }

  return (
    <Modal title={`صلاحيات — ${target.name}`} onClose={onClose} width={640}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={S.rolesToggleList}>
          {allRoles.map(r => {
            const on = sel.includes(r);
            const locked = lockSystem && r === "system";
            return (
              <div
                key={r}
                onClick={() => toggle(r)}
                style={{
                  ...S.roleToggleRow,
                  ...(on ? S.roleToggleRowOn : null),
                  cursor: locked ? "not-allowed" : "pointer",
                  opacity: locked ? 0.65 : 1,
                }}
              >
                <div style={{ ...S.toggleTrack, background: on ? "#7c3aed" : C.border }}>
                  <div style={{ ...S.toggleKnob, right: on ? 2 : 20 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: on ? 700 : 600, color: on ? "#7c3aed" : C.primary }}>
                    {ROLE_NAMES[r]}
                    {locked && <span style={{ fontSize: 10, color: C.textLight, fontWeight: 500 }}> — لا يمكن إزالته من حسابك</span>}
                  </div>
                  <div style={{ fontSize: 11, color: C.textLight, lineHeight: 1.5 }}>{ROLE_DESCRIPTIONS[r]}</div>
                </div>
              </div>
            );
          })}
        </div>

        <PermissionsPreview roles={sel} />

        {sel.length === 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: `${C.danger}10`, color: C.danger, borderRadius: 8, fontSize: 12.5 }}>
            <AlertCircle size={15} />
            يجب اختيار دور واحد على الأقل
          </div>
        )}

        <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          <Btn icon={Shield} onClick={handleSave} disabled={saving || sel.length === 0}>
            {saving ? "جارٍ الحفظ..." : "حفظ التعديلات"}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ─── Roles Tab ─── */
function RolesTab() {
  const allRoles = Object.keys(ROLE_NAMES);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={S.kpiRow}>
        <KPICard title="عدد الأدوار" value={allRoles.length} icon={Shield} color="#7c3aed" />
        <KPICard title="أدوار إدارية" value={allRoles.filter(r => ROLE_PERMISSIONS[r]?.adminOnly).length} icon={Lock} color={C.danger} />
        <KPICard title="أدوار تحرير" value={allRoles.filter(r => ROLE_PERMISSIONS[r]?.canEdit).length} icon={Edit} color={C.teal} />
      </div>

      <div style={S.rolesGrid}>
        {allRoles.map(roleKey => {
          const perm = ROLE_PERMISSIONS[roleKey] || {};
          return (
            <Card key={roleKey} style={S.roleCard}>
              <div style={S.roleHeader}>
                <div style={{ ...S.roleIcon, background: perm.adminOnly ? `${C.danger}12` : `#7c3aed12` }}>
                  {perm.adminOnly ? <Lock size={20} color={C.danger} /> : <Shield size={20} color="#7c3aed" />}
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{ROLE_NAMES[roleKey]}</h4>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight, fontFamily: "monospace" }}>{roleKey}</p>
                </div>
              </div>

              {/* Permission badges */}
              <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                {perm.adminOnly && <span style={{ ...S.permBadge, background: `${C.danger}15`, color: C.danger }}>مشرف كامل</span>}
                {perm.canEdit && <span style={{ ...S.permBadge, background: `${C.teal}15`, color: C.teal }}>تعديل</span>}
                {perm.canSign && <span style={{ ...S.permBadge, background: `${C.accent}15`, color: "#8b6914" }}>توقيع واعتماد</span>}
                {!perm.canEdit && !perm.adminOnly && <span style={{ ...S.permBadge, background: `${C.textLight}15`, color: C.textLight }}>قراءة فقط</span>}
              </div>

              {/* Modules access */}
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.textLight }}>الوصول للموديولات ({perm.modules?.length || 0}):</span>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                  {(perm.modules || []).map((m, i) => (
                    <span key={i} style={S.moduleTag}>{m}</span>
                  ))}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Audit Tab ─── */
function AuditTab() {
  return (
    <Card style={{ padding: 32, textAlign: "center", marginTop: 16 }}>
      <div style={{ fontSize: 16, color: C.primary, marginBottom: 8 }}>
        سجل المراجعة قيد التطوير
      </div>
      <div style={{ fontSize: 13, color: C.textLight }}>
        سيُتاح في إصدار قادم — يعرض جميع الإجراءات الإدارية مع التواريخ والمنفّذين
      </div>
    </Card>
  );
}

/* ─── Sessions Tab ─── */
function SessionsTab() {
  return (
    <Card style={{ padding: 32, textAlign: "center", marginTop: 16 }}>
      <div style={{ fontSize: 16, color: C.primary, marginBottom: 8 }}>
        إدارة الجلسات قيد التطوير
      </div>
      <div style={{ fontSize: 13, color: C.textLight }}>
        سيُتاح في إصدار قادم — يعرض الجلسات النشطة مع إمكانية إنهائها
      </div>
    </Card>
  );
}

/* ═════ Styles ═════ */
const S = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  tableAvatar: { width: 32, height: 32, borderRadius: 8, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 },
  userHeader: { display: "flex", alignItems: "center", gap: 20, paddingBottom: 14, borderBottom: `1px solid ${C.border}` },
  userAvatarLg: { width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 22, flexShrink: 0 },
  actionBtn: { background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" },
  permTag: { padding: "3px 8px", background: `#7c3aed10`, color: "#7c3aed", borderRadius: 6, fontSize: 11, fontWeight: 500 },
  permBadge: { padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600 },

  // ── الأدوار المتعددة ──
  rolePrimary: { padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: C.primary, color: C.white, whiteSpace: "nowrap" },
  roleExtra: { padding: "3px 7px", borderRadius: 6, fontSize: 10.5, fontWeight: 600, background: `${C.teal}18`, color: C.teal, whiteSpace: "nowrap" },
  sectionLabel: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: C.primary, marginBottom: 8 },
  rolesCheckGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 },
  roleCheckItem: { display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 8, border: `1px solid ${C.border}`, cursor: "pointer", background: C.white, color: C.primary },
  roleCheckItemOn: { border: `1px solid #7c3aed`, background: "#7c3aed0d", color: "#7c3aed" },
  rolesToggleList: { display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto", paddingLeft: 4 },
  roleToggleRow: { display: "flex", alignItems: "center", gap: 12, padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white },
  roleToggleRowOn: { border: `1px solid #7c3aed`, background: "#7c3aed0a" },
  toggleTrack: { position: "relative", width: 38, height: 20, borderRadius: 999, flexShrink: 0, transition: "background .18s" },
  toggleKnob: { position: "absolute", top: 2, width: 16, height: 16, borderRadius: "50%", background: C.white, boxShadow: "0 1px 3px rgba(0,0,0,.25)", transition: "right .18s" },
  previewBox: { padding: 12, borderRadius: 10, background: C.bg, border: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 8 },
  previewTitle: { fontSize: 12.5, fontWeight: 700, color: C.primary },
  previewRow: { display: "flex", gap: 10, alignItems: "flex-start" },
  previewKey: { fontSize: 12, color: C.textLight, fontWeight: 600, minWidth: 66, flexShrink: 0, paddingTop: 2 },

  rolesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  roleCard: { padding: 18 },
  roleHeader: { display: "flex", alignItems: "center", gap: 12 },
  roleIcon: { width: 44, height: 44, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  moduleTag: { padding: "2px 7px", background: C.bg, color: C.textLight, borderRadius: 4, fontSize: 10, fontWeight: 500, border: `1px solid ${C.border}` },
};
